import React, { useEffect, useMemo, useState } from 'react';
import { FaDownload, FaEye, FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  defaultColumnsBySection,
  filterConfigBySection,
  sectionNavItems
} from './reportSectionConfig';

const prettyLabel = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value, column) => {
  if (column === 'status') {
    if (typeof value === 'string' && Number.isNaN(Number(value))) {
      return value.replace('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }
    const normalizedStatus = parseInt(value, 10);
    if (normalizedStatus === 1) return 'Active';
    if (normalizedStatus === 0) return 'Inactive';
  }
  if (['amount', 'final_amount', 'score'].includes(column) && value !== null && value !== undefined && value !== '') {
    const num = Number(value);
    if (!Number.isNaN(num)) return num.toFixed(2);
  }
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' && value.includes('T')) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  }
  return value;
};

const ReportSectionPage = ({ scope, section, title }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [filters, setFilters] = useState({});
  const [courses, setCourses] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [learnerModal, setLearnerModal] = useState({ open: false, loading: false, course: null, learners: [] });
  const [licenseModal, setLicenseModal] = useState({ open: false, loading: false, org: null, requests: [] });

  const columns = useMemo(() => defaultColumnsBySection[section] || [], [section]);
  const basePath = scope === 'super-admin' ? '/admin/reports' : '/organization/reports';
  const navItems = useMemo(() => sectionNavItems(basePath, scope), [basePath, scope]);
  const filterConfig = filterConfigBySection[section] || [];
  const isSuperAdmin = scope === 'super-admin';

  const buildQueryParams = (nextPage = 1) => {
    const params = { page: nextPage, limit: 10 };
    if (search.trim()) params.search = search.trim();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  };

  const fetchData = async (nextPage = 1) => {
    try {
      setLoading(true);
      const response = await api.reports.getSection(scope, section, buildQueryParams(nextPage));
      setItems(response.data?.data?.items || []);
      setPagination(response.data?.data?.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading report section:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        if (filterConfig.some((filter) => filter.type === 'course')) {
          const courseResponse = await api.courses.getAll(isSuperAdmin);
          setCourses(courseResponse.data?.data || courseResponse.data?.courses || []);
        }
        if (filterConfig.some((filter) => filter.type === 'organization') && isSuperAdmin) {
          const orgResponse = await api.organizations.getAll();
          setOrganizations(orgResponse.data?.data || orgResponse.data?.organizations || []);
        }
      } catch (error) {
        console.error('Error loading report filter options:', error);
      }
    };

    loadFilterOptions();
  }, [section, scope, isSuperAdmin]);

  useEffect(() => {
    fetchData(1);
  }, [scope, section]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchSubmit = () => {
    fetchData(1);
  };

  const handleApplyFilters = () => {
    fetchData(1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = buildQueryParams(1);
      delete params.page;
      delete params.limit;
      const response = await api.reports.exportSection(scope, section, params);
      downloadBlob(response.data, `${scope}_${section}_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting report section:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleEnrollmentRowClick = async (item) => {
    if (section !== 'course-enrollments') return;
    try {
      setLearnerModal({ open: true, loading: true, course: item, learners: [] });
      const params = { course_id: item.course_id };
      if (filters.organization_id) params.organization_id = filters.organization_id;
      const response = await api.reports.getEnrollmentLearners(scope, params);
      setLearnerModal({
        open: true,
        loading: false,
        course: response.data?.data || { course_name: item.course_name, learners: [] },
        learners: response.data?.data?.learners || []
      });
    } catch (error) {
      console.error('Error loading enrolled learners:', error);
      toast.error('Failed to load enrolled learners');
      setLearnerModal({ open: false, loading: false, course: null, learners: [] });
    }
  };

  const handleViewLicenseRequests = async (item) => {
    try {
      setLicenseModal({ open: true, loading: true, org: item, requests: [] });
      const response = await api.reports.getOrgLicenseRequests(scope, item.organization_id);
      setLicenseModal({
        open: true,
        loading: false,
        org: item,
        requests: response.data?.data?.items || []
      });
    } catch (error) {
      console.error('Error loading license requests:', error);
      toast.error('Failed to load license requests');
      setLicenseModal({ open: false, loading: false, org: null, requests: [] });
    }
  };

  const showOrgFilterForUsers = ['org_learner', 'org_instructor'].includes(filters.user_type);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          {title}
        </h2>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="border border-green-600 bg-white text-green-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm py-2 px-4 flex items-center justify-center gap-2"
        >
          <FaDownload className="w-4 h-4" />
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => navigate(item.path)}
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              section === item.key
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {filterConfig.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {filterConfig.map((filter) => {
            if (filter.type === 'select') {
              return (
                <div key={filter.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{filter.label}</label>
                  <select
                    value={filters[filter.key] || ''}
                    onChange={(event) => handleFilterChange(filter.key, event.target.value)}
                    className="w-full rounded-lg border border-purple-200 px-3 py-2 text-sm bg-white"
                  >
                    {filter.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              );
            }

            if (filter.type === 'course') {
              return (
                <div key={filter.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{filter.label}</label>
                  <select
                    value={filters[filter.key] || ''}
                    onChange={(event) => handleFilterChange(filter.key, event.target.value)}
                    className="w-full rounded-lg border border-purple-200 px-3 py-2 text-sm bg-white"
                  >
                    <option value="">All Courses</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>{course.name || course.title}</option>
                    ))}
                  </select>
                </div>
              );
            }

            if (filter.type === 'organization' && isSuperAdmin) {
              return (
                <div key={filter.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{filter.label}</label>
                  <select
                    value={filters[filter.key] || ''}
                    onChange={(event) => handleFilterChange(filter.key, event.target.value)}
                    className="w-full rounded-lg border border-purple-200 px-3 py-2 text-sm bg-white"
                  >
                    <option value="">{filter.optional ? 'All Organizations' : 'All'}</option>
                    {filter.includeIndividual && <option value="individual">Individual Users</option>}
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              );
            }

            return null;
          })}

          {section === 'users' && showOrgFilterForUsers && isSuperAdmin && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Organization</label>
              <select
                value={filters.organization_id || ''}
                onChange={(event) => handleFilterChange('organization_id', event.target.value)}
                className="w-full rounded-lg border border-purple-200 px-3 py-2 text-sm bg-white"
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end">
            <button
              type="button"
              onClick={handleApplyFilters}
              className="w-full border border-purple-600 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition text-sm py-2 px-4"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="flex shadow-sm rounded-xl overflow-hidden bg-white flex-1">
          <span className="inline-flex items-center px-3 text-purple-600 bg-white border-r border-purple-200">
            <FaSearch className="w-4 h-4" />
          </span>
          <input
            type="text"
            placeholder="Search report..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleSearchSubmit()}
            className="border-0 focus:ring-2 focus:ring-purple-500 text-sm form-control w-full px-3 py-2"
          />
        </div>
        <button
          type="button"
          onClick={handleSearchSubmit}
          className="border border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm py-2 px-4"
        >
          Search
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-80">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-xl bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                {columns.map((column) => (
                  <th key={column} className="py-3 px-3 text-left font-bold whitespace-nowrap">
                    {prettyLabel(column)}
                  </th>
                ))}
                {section === 'organizations' && isSuperAdmin && (
                  <th className="py-3 px-3 text-left font-bold">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (section === 'organizations' && isSuperAdmin ? 1 : 0)} className="text-center py-4 text-gray-500">
                    No report data found.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr
                    key={item.id || item.ticket_id || item.voucher_id || item.course_id || item.organization_id || item.payment_id || index}
                    className={`border-b border-purple-100 hover:bg-purple-50/50 transition ${
                      section === 'course-enrollments' ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => section === 'course-enrollments' && handleEnrollmentRowClick(item)}
                    onKeyDown={(event) => {
                      if (section === 'course-enrollments' && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        handleEnrollmentRowClick(item);
                      }
                    }}
                    tabIndex={section === 'course-enrollments' ? 0 : undefined}
                    aria-label={section === 'course-enrollments' ? `View enrolled learners for ${item.course_name}` : undefined}
                  >
                    {columns.map((column) => (
                      <td key={`${index}-${column}`} className="py-3 px-3 text-gray-700 whitespace-nowrap">
                        {formatValue(item[column], column)}
                      </td>
                    ))}
                    {section === 'organizations' && isSuperAdmin && (
                      <td className="py-3 px-3">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleViewLicenseRequests(item);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 rounded-full hover:bg-purple-100"
                        >
                          <FaEye className="w-3 h-3" />
                          View Requests
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={() => fetchData(Math.max(page - 1, 1))}
          disabled={page <= 1}
          className="border border-purple-300 bg-white text-purple-700 rounded-md px-3 py-1 disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-sm text-gray-700">
          Page {pagination.page || 1} of {pagination.totalPages || 1}
        </span>
        <button
          type="button"
          onClick={() => fetchData(Math.min(page + 1, pagination.totalPages || 1))}
          disabled={page >= (pagination.totalPages || 1)}
          className="border border-purple-300 bg-white text-purple-700 rounded-md px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {learnerModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-purple-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Enrolled Learners</h3>
                <p className="text-sm text-gray-500">{learnerModal.course?.course_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setLearnerModal({ open: false, loading: false, course: null, learners: [] })}
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Close learners modal"
              >
                <FaTimes />
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              {learnerModal.loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent" />
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Name</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Organization</th>
                      <th className="px-4 py-3 text-left">Enrolled On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learnerModal.learners.length > 0 ? (
                      learnerModal.learners.map((learner) => (
                        <tr key={learner.user_id} className="border-t border-gray-100">
                          <td className="px-4 py-3">{learner.full_name}</td>
                          <td className="px-4 py-3">{learner.email}</td>
                          <td className="px-4 py-3">{learner.organization_name}</td>
                          <td className="px-4 py-3">{formatValue(learner.enrollment_date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No enrolled learners found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {licenseModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-purple-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">License Requests</h3>
                <p className="text-sm text-gray-500">{licenseModal.org?.organization_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setLicenseModal({ open: false, loading: false, org: null, requests: [] })}
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Close license requests modal"
              >
                <FaTimes />
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              {licenseModal.loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent" />
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Requested</th>
                      <th className="px-4 py-3 text-left">Approved</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Requested By</th>
                      <th className="px-4 py-3 text-left">Approved By</th>
                      <th className="px-4 py-3 text-left">Requested At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenseModal.requests.length > 0 ? (
                      licenseModal.requests.map((request) => (
                        <tr key={request.request_id} className="border-t border-gray-100">
                          <td className="px-4 py-3">{request.requested_licenses}</td>
                          <td className="px-4 py-3">{request.approved_licenses}</td>
                          <td className="px-4 py-3 capitalize">{request.status}</td>
                          <td className="px-4 py-3">{request.requested_by_name || '—'}</td>
                          <td className="px-4 py-3">{request.approved_by_name || '—'}</td>
                          <td className="px-4 py-3">{formatValue(request.requested_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No license requests found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSectionPage;
